import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useProducts } from "@/lib/api/hooks";
import { PageHeader } from "@/components/layout/page-header";
import { Plus, Pencil, Trash2, MoreHorizontal, Package } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Badge } from "@/components/ui/badge";
import { Product } from "@/lib/api/types";
import { PermissionGuard } from "@/components/guards/PermissionGuard";

export default function StoreProductsPage() {
    const { products, isLoading, delete: deleteProduct } = useProducts();
    const navigate = useNavigate();
    const [productToDelete, setProductToDelete] = useState<Product | null>(null);

    const handleDelete = async () => {
        if (!productToDelete) return;
        try {
            await deleteProduct(productToDelete.id);
            setProductToDelete(null);
        } catch (error) {
        }
    };

    const formatPrice = (priceInCents: number) => {
        return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(priceInCents / 100);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-muted-foreground">Loading products...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Products"
                action={
                    <PermissionGuard resource="products" action="create">
                        <Button onClick={() => navigate("/store/products/new")}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Product
                        </Button>
                    </PermissionGuard>
                }
            />

            {!products || products.length === 0 ? (
                <EmptyState
                    icon={Package}
                    title="No products yet"
                    description="Create your first product to start selling"
                    action={{
                        label: "Add Product",
                        onClick: () => navigate("/store/products/new"),
                    }}
                />
            ) : (
                <div className="border rounded-lg bg-card">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[80px]">Image</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead>Stock</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-[80px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {products.map((product) => (
                                <TableRow key={product.id}>
                                    <TableCell>
                                        {product.imageUrl ? (
                                            <div className="w-12 h-12 rounded-md overflow-hidden bg-muted">
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center">
                                                <div className="w-6 h-6 bg-muted-foreground/20 rounded" />
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="font-medium">{product.name}</TableCell>
                                    <TableCell className="capitalize text-muted-foreground">
                                        {product.category || 'Uncategorized'}
                                    </TableCell>
                                    <TableCell>{formatPrice(product.price)}</TableCell>
                                    <TableCell>
                                        {product.stockQuantity === 0 ? (
                                            <Badge variant="destructive">Out of Stock</Badge>
                                        ) : (
                                            <span className="text-muted-foreground">{product.stockQuantity}</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {product.isActive ? (
                                            <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
                                        ) : (
                                            <Badge variant="secondary" className="bg-gray-100 text-gray-800 hover:bg-gray-100">Inactive</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" className="h-8 w-8 p-0">
                                                    <span className="sr-only">Open menu</span>
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <PermissionGuard resource="products" action="update">
                                                    <DropdownMenuItem onClick={() => navigate(`/store/products/${product.id}`)}>
                                                        <Pencil className="mr-2 h-4 w-4" />
                                                        Edit
                                                    </DropdownMenuItem>
                                                </PermissionGuard>
                                                <PermissionGuard resource="products" action="delete">
                                                    <DropdownMenuItem
                                                        className="text-red-600 focus:text-red-600"
                                                        onClick={() => setProductToDelete(product)}
                                                    >
                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                        Delete
                                                    </DropdownMenuItem>
                                                </PermissionGuard>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <ConfirmationDialog
                isOpen={!!productToDelete}
                onClose={() => setProductToDelete(null)}
                onConfirm={handleDelete}
                title="Delete Product"
                description={`Are you sure you want to delete "${productToDelete?.name}"? It will be marked as inactive.`}
                confirmText="Delete"
                variant="destructive"
            />
        </div>
    );
}
